<template>
    <ion-page>
        <ion-header>
            <ion-toolbar color="primary">
                <ion-buttons slot="start">
                    <ion-back-button text="Retour" :default-href="`/report-sheets/${sheet?.uuid}`"></ion-back-button>
                </ion-buttons>
                <ion-title>Rapport : {{ sheet?.title }}</ion-title>
            </ion-toolbar>
        </ion-header>

        <div :class="$style['sub-header']">Locations ({{ sheet?.locations.length ?? 0 }})</div>

        <ion-content>
            <ContentSidebarWrapper side="left">
                <template v-slot:content>
                    <div v-if="!loaded">
                        <EquipmentSkeleton/>
                    </div>
                    <div v-else-if="sheet?.locations?.length" style="padding-bottom: 200px;">
                        <ion-accordion-group>
                            <ion-accordion v-for="location in sheet?.locations" :key="location.uuid" :value="location.uuid">
                                <ion-item slot="header" color="light">
                                    <ion-label>
                                        {{ location?.name }}
                                        <span v-if="location.total_cost " :class="$style['total_cost']">Total : {{ location.total_cost ?? 0.00 }}€</span>
                                    </ion-label>
                                </ion-item>
                                
                                <div slot="content"
                                @mouseover="()=>{providerFilter={equipmentUuid:location.equipment.uuid}}"
                                >
                                    <ion-item>
                                        <UnitySelector @ionChange="handleUnity(location, $event)" :disabled="fieldDisabled" v-model="location.unity" />
                                    </ion-item>

                                    <ion-item>
                                        <ion-input :disabled="fieldDisabled" label="Quantité :" placeholder="Enter data" v-model="location.quantity" @change="calculateTotalDailyCost(location)"></ion-input>
                                    </ion-item>

                                    <ion-item>
                                        <ion-input @click="triggerUserSelector(location)" :disabled="fieldDisabled" label="Employé :" placeholder="Select data" :value="`${location.employee_firstname} ${location.employee_lastname}`"></ion-input>

                                        <span v-if="location.employee_firstname || location.employee_lastname" :class="$style['container-button-remove-employee']">
                                            <ion-button fill="outline" size="small" @click="removeEmployee(location)" slot="end">
                                                <ion-icon :class="$style['icon-remove-employee']" :icon="personRemoveOutline"></ion-icon>
                                            </ion-button>
                                        </span>
                                    </ion-item>

                                    <ion-item>
                                        <ion-input @click="triggerProviderSelector(location)" :disabled="fieldDisabled" label="Fournisseur :" placeholder="Select data" :value="location.provider_name"></ion-input>

                                        <span v-if="location.provider_name" :class="$style['container-button-remove-employee']">
                                            <ion-button fill="outline" size="small" @click="removeProvider(location)" slot="end">
                                                <ion-icon :class="$style['icon-remove-employee']" :icon="personRemoveOutline"></ion-icon>
                                            </ion-button>
                                        </span>
                                    </ion-item>

                                    <ion-item  v-if="user.rights?.includes('report_sheets.manager')">
                                        <ion-input :disabled="fieldDisabled" :label="location.unity === 'Hr' ? 'Coût horaire' : 'Coût unitaire'" v-model="location.hourly_cost" type="number" @change="calculateTotalDailyCost(location)"></ion-input>
                                    </ion-item>

                                    <ion-item v-if="user.rights?.includes('report_sheets.manager')">
                                        <ion-input :disabled="true" label="Coût total" v-model="location.total_cost"></ion-input>
                                    </ion-item>

                                    <div v-if="!fieldDisabled" :class="$style['container-button']">
                                        <ion-button v-if="false" slot="start" size="small" fill="outline" @click="presentActionEquipment(location)">Supprimer</ion-button>
                                        <ion-button slot="end" size="small" color="success" @click="formSubmit(location)">Enregistrer</ion-button>
                                    </div>
                                </div>
                            </ion-accordion>
                        </ion-accordion-group>
                    </div>
                    <div v-else>
                        <ion-text class="ion-text-center" color="medium">
                            <p>Aucun résultat</p>
                        </ion-text>
                    </div>
                </template>

                <template v-slot:sidebar v-if="loaded && sheet.total_locations_cost ">
                    <p :class="$style['total_day']">Coût total des locations : {{ sheet.total_locations_cost ?? 0.00 }}€</p>
                </template>
            </ContentSidebarWrapper>

            <template v-if="!sheet?.locations_step_validated || userCanForceEdit()">
                <ion-fab slot="fixed" vertical="bottom" horizontal="end">
                    <ion-fab-button color="primary" @click="presentActionSheet">
                        <ion-icon :icon="caretUp"></ion-icon>
                    </ion-fab-button>
                </ion-fab>

                <EquipmentSelector :active="equipmentSelectorOpen" @change="handleEquipmentSelection" @close="equipmentSelectorOpen=false" :filter="{ types: ['renting'] }"/>
                <UserSelectorButton style="display: none;" ref="userSelectorRef" @change="selectEmployee($event.value)" />
                <ProviderSelector :active="providerSelectorOpen" @change="handleProviderSelection" @close="providerSelectorOpen=false" :filter="providerFilter" />
            </template>
        </ion-content>
    </ion-page>
</template>

<script lang="ts">
    import {
        IonBackButton,
        IonButtons,
        IonHeader,
        IonPage,
        IonToolbar,
        IonItem,
        IonContent,
        toastController,
        IonFab,
        IonFabButton,
        actionSheetController,
        alertController,
        IonIcon,
        IonAccordionGroup,
        IonAccordion,
        IonInput
    } from "@ionic/vue"

    import {
        caretUp, personRemoveOutline
    } from 'ionicons/icons'

    import { defineComponent } from "vue"
    import EquipmentSkeleton from "@/components/skeletons/EquipmentSkeleton.vue";
    import ContentSidebarWrapper from "@/components/wrappers/ContentSidebarWrapper.vue"
    import { ReportSheet } from "@/report-sheets"
    import EquipmentSelector from "@/components/data/selectors/EquipmentSelector.vue"
    import { useUserStore } from "@/stores/user"
    import { Equipment } from "@/equipments";
    import { Location } from "@/locations";
    import { User } from "@/users";
    import { OrderProvider } from "@/orders";
    import UserSelectorButton from "@/components/data/selectors/UserSelectorButton.vue";
    import ProviderSelector from "@/components/data/selectors/ProviderSelector.vue";
    import UnitySelector from "@/components/forms/ui/UnitySelector.vue";

    export default defineComponent({
        components: {
            EquipmentSelector,
            EquipmentSkeleton,
            UserSelectorButton,
            UnitySelector,
            ProviderSelector,
            ContentSidebarWrapper,
            IonBackButton,
            IonButtons,
            IonHeader,
            IonItem,
            IonPage,
            IonToolbar,
            IonContent,
            IonFab,
            IonFabButton,
            IonIcon,
            IonAccordionGroup,
            IonAccordion,
            IonInput
        },
        setup() {
            const user = useUserStore()

            return { 
                caretUp,
                user,
                personRemoveOutline
            }
        },
        data() {
            return {
                fieldDisabled: true,
                loaded: false as boolean | false,
                sheet: null as ReportSheet | null,
                locationEditingUuid: null as string | null,
                selectedEquipmentUuid: null as string | null,
                equipmentSelectorOpen: false,
                providerSelectorOpen: false,
                providerFilter: null as { equipmentUuid?: string } | null,
                selectedEquipment: null as Equipment | null
            }
        },
        mounted() {
            if (this.user.is_logged_in)
                this.load()

            this.$watch("user.is_logged_in", (is_logged_in: boolean) => {
                if (is_logged_in) this.load();
            });
        },
        methods: {
            calculateTotalDailyCost(location: Location) {
                const hourlyCost = location.hourly_cost ? parseFloat(location.hourly_cost) : 0
                const quantity = location.quantity ? parseFloat(location.quantity) : 0

                const totalCost = quantity * hourlyCost
                location.total_cost = totalCost.toFixed(2)
            },
            load() {
                this.loaded = false
                
                this.axios.get(`/report-sheets/${this.$route.params.uuidReport}`)
                .then((responses) => {
                    this.sheet = responses.data

                    if (this.sheet && (!this.sheet.locations_step_validated || this.userCanForceEdit())) 
                        this.fieldDisabled = false

                    if (this.sheet) {
                        this.sheet.locations = this.sheet.locations.map((location: Location) => {
                            location.employee_firstname = location.employee_firstname ?? ''
                            location.employee_lastname = location.employee_lastname ?? ''
                            location.provider_uuid = location.provider_uuid ?? ''
                            location.provider_name = location.provider_name ?? ''

                            return location
                        })
                    }
                }).catch((error) => {
                    console.error(error)
                }).finally(() => {
                    this.loaded = true
                })
            },
            userCanForceEdit(): boolean {
                if (!this.sheet || !this.user) return false
                
                // Get user's role for this report sheet
                const userRole = this.sheet.roles?.find(role => role.user?.uuid === this.user.uuid)?.role
                
                if (!userRole) return false
                
                // Site-supervisor and manager can always modify, regardless of validation status
                if (['site-supervisor', 'manager'].includes(userRole)) {
                    return true
                }
                
                // Site-manager can modify if the report is not validated by site-manager or manager
                if (userRole === 'site-manager') {
                    return !this.sheet.validated_by_site_manager && !this.sheet.validated_by_manager
                }
                
                // Other roles can only modify if no validation has been done
                return !this.sheet.validated_by_site_supervisor && !this.sheet.validated_by_site_manager && !this.sheet.validated_by_manager
            },
            formSubmit(location: Location) {
                if (!this.loaded)
                    return
                
                this.loaded = false

                this.axios.put(`/report-sheets/${this.$route.params.uuidReport}/locations/${location.uuid}`, {
                    unity: location?.unity,
                    quantity: location?.quantity,
                    employee_firstname: location?.employee_firstname,
                    employee_lastname: location?.employee_lastname,
                    hourly_cost: location?.hourly_cost,
                    provider_uuid: location?.provider_uuid
                }).then(async () => {
                    (
                        await toastController.create({
                            header: 'SUCCESS',
                            message: 'Correctly updated',
                            duration: 5000,
                            color: "success"
                        })
                    ).present()
                }).catch((error) => {
                    console.error(error)
                }).finally(() => {
                    this.load()
                })
            },
            addLocation(equipment: Equipment) {
                this.loaded = false

                this.axios.post(`/report-sheets/${this.$route.params.uuidReport}/locations`, {
                    locationUuid: equipment.uuid,
                    providerUuid: equipment.provider_uuid
                }).then(async (response) => {
                    (
                        await toastController.create({
                            header: 'SUCCESS',
                            message: 'Location added',
                            duration: 5000,
                            color: "success"
                        })
                    ).present()

                    if (this.sheet) {
                        const location = response.data
                        location.employee_firstname = location.employee_firstname ?? ''
                        location.employee_lastname = location.employee_lastname ?? ''
                        location.provider_uuid = location.provider_uuid ?? ''
                        location.provider_name = location.provider_name ?? ''
                    }
                }).catch((error) => {
                    console.error(error)
                }).finally(() => {
                    this.load()
                })
            },
            stepValidation() {
                this.loaded = false

                this.axios.put(`/report-sheets/${this.$route.params.uuidReport}/step-validation`, {
                    step: 'locations_step_validated'
                }).then(async () => {
                    (
                        await toastController.create({
                            header: 'SUCCESS',
                            message: 'Locations step validated',
                            duration: 5000,
                            color: "success"
                        })
                    ).present()

                    this.$router.push(`/report-sheets/${this.$route.params.uuidReport}`)
                }).catch((error) => {
                    console.error(error)
                    this.loaded = true
                })
            },
            removeEmployee(location: Location) {
                location.employee_firstname = ''
                location.employee_lastname = ''
            },
            selectEmployee(user: User) {
                if (this.sheet) {
                    const index = this.sheet.locations.findIndex((e: Location) => e.uuid === this.locationEditingUuid)

                    this.sheet.locations[index].employee_firstname = user.first_name ?? ''
                    this.sheet.locations[index].employee_lastname = user.last_name ?? ''
                }
            },
            selectProvider(provider: OrderProvider) {
                if (this.sheet) {
                    const index = this.sheet.locations.findIndex((e: Location) => e.uuid === this.locationEditingUuid)

                    this.sheet.locations[index].provider_uuid = provider.uuid ?? ''
                    this.sheet.locations[index].provider_name = provider.name ?? ''
                }
            },
            triggerUserSelector(location: Location) {
                this.locationEditingUuid = location.uuid

                const userSelector = this.$refs.userSelectorRef as any
                
                if (userSelector?.openSelector)
                    userSelector.openSelector()
            },
            triggerProviderSelector(location: Location) {
                this.locationEditingUuid = location.uuid
                this.providerSelectorOpen = true
            },
            removeProvider(location: Location) {
                location.provider_uuid = ''
                location.provider_name = ''
            },
            handleUnity(location: Location, event: any) {
                location.quantity = event.detail.value === 'Hr' ? '8' : '1'

                this.calculateTotalDailyCost(location)
            },
            triggerLocationSelector() {
                this.equipmentSelectorOpen = true
            },
            handleEquipmentSelection(event: any) {
                const equipment = event.value as Equipment

                    // Store equipment and open provider selector with filter
                    this.selectedEquipment = equipment
                    this.providerFilter = { equipmentUuid: equipment.uuid }
                    this.providerSelectorOpen = true

            },
            handleProviderSelection(event: any) {
                const provider = event.value as OrderProvider

                // Updating existing location's provider
                if (this.locationEditingUuid) {
                    this.selectProvider(provider)
                    return
                }

                // Adding new location with equipment
                if (!this.selectedEquipment) return

                const equipmentWithProvider = {
                    ...this.selectedEquipment,
                    provider_uuid: provider.uuid
                }

                this.addLocation(equipmentWithProvider)

                this.selectedEquipment = null
                this.providerFilter = null
            },
            deleteItem(location: Location) {
                if (!this.loaded)
                    return

                this.loaded = false
                
                this.axios.delete(`/report-sheets/${this.$route.params.uuidReport}/locations/${location.uuid}`)
                .then(async () => {
                    (
                        await toastController.create({
                            header: 'SUCCESS',
                            message: 'Correctly deleted',
                            duration: 5000,
                            color: "success"
                        })
                    ).present()

                    if (this.sheet)
                        this.sheet.locations = this.sheet.locations.filter((e: Location) => e.uuid !== location.uuid)
                }).catch((error) => {
                    console.error(error)
                }).finally(() => {
                    this.loaded = true
                })
            },
            async presentActionEquipment(location: Location) {
                const alert = await alertController.create({
                    message: 'Etes-vous sûr de vouloir supprimer définitivement ?',
                    htmlAttributes: {
                        'aria-label': 'alert dialog'
                    },
                    buttons: [
                        {
                            text: 'Annuler',
                            role: "cancel"
                        },
                        {
                            text: 'Confirmer',
                            role: "confirm",
                            handler: () => {
                                this.deleteItem(location)
                            }
                        }
                    ]
                })

                await alert.present()
            },
            async presentActionSheet() {
                const actionSheet = await actionSheetController.create({
                    buttons: [
                        {
                            text: "Ajouter une location",
                            handler: () => {
                                this.triggerLocationSelector()
                            }
                        },
                        {
                            text: "Valider cette étape",
                            handler: async () => {
                                const alert = await alertController.create({
                                    message: 'Etes-vous sûr de vouloir valider cette étape ?',
                                    htmlAttributes: {
                                        'aria-label': 'alert dialog'
                                    },
                                    buttons: [
                                        {
                                            text: 'Annuler',
                                            role: "cancel"
                                        },
                                        {
                                            text: 'Confirmer',
                                            role: "confirm",
                                            handler: () => {
                                                this.stepValidation()
                                            }
                                        }
                                    ]
                                })

                                await alert.present()
                            }
                        },
                        {
                            text: "Annuler",
                            role: "cancel"
                        }
                    ]
                })

                await actionSheet.present()
            }
        }
    })
</script>

<style lang="scss" module>
    .sub-header {
        background: #000;
        color:white;
        height: 50px;
        width: 100%;
        display: grid;
        align-content: center;
        justify-content: center;
    }
    .inner {
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        justify-content: space-between;
        padding: 1em;
        font-size: .9em;

        @media(max-width: 990px) {
            font-size: .8em;
        }
        @media(max-width: 700px) {
            flex-direction: column
        }
        h4, p {
            padding: 0;
            margin: 0;
        }
        p {
            margin-top: .25em;
            color: var(--ion-color-medium);
        }
        .right {
            display: flex;
            align-items: center;

            @media(min-width: 701px) {
                flex-direction: column;
                align-items: flex-end;
            }

            @media(max-width: 700px) {
                .date {
                    &:before {
                        content: '—';
                        margin: 0 .5em;
                        margin-top: 0;
                    }
                }
            }
        }
    }
    ion-item {
        --inner-padding-end: 0;
        --padding-end: 16px;
    }
    ion-select {
        justify-content: flex-start;
    }
    .container-button {
        width: 100%;
        display: flex;
        justify-content: space-between;
        padding: 12px;
    }
    .total_cost {
        display: block;
        margin-top: .25em;
        color: var(--ion-color-medium);
    }
    .total_day {
        color: #fff;
        background: #9e9e9e;
        margin: 0;
        padding: 10px;
        text-align: center;
    }
</style>